import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search } from 'lucide-react';
import {
  Button,
  Table,
  Modal,
  Pagination,
  SearchBar,
  Loading,
  Alert,
  Card,
} from '../../components/common';
import GlosarioForm from '../../components/glosario/GlosarioForm';
import TerminoDetail from '../../components/glosario/TerminoDetail';
import { glosarioAPI } from '../../api';

const GlosarioPage = () => {
  // Estado
  const [terminos, setTerminos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    categoria: '',
    letra: '',
  });

  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('termino');
  const [sortDirection, setSortDirection] = useState('asc');

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTermino, setSelectedTermino] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Categorías para filtros
  const categorias = [
    'Ética General',
    'Ética Profesional',
    'Bioética',
    'Ética Digital',
    'Derechos Humanos',
    'Responsabilidad Social',
    'Integridad',
    'Transparencia',
    'Compliance',
    'Otros',
  ];

  // Letras para navegación alfabética
  const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Cargar términos
  useEffect(() => {
    loadTerminos();
  }, [currentPage, itemsPerPage, searchTerm, filters, sortColumn, sortDirection]);

  const loadTerminos = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Búsqueda FULLTEXT si hay término
      if (searchTerm) {
        params.buscar = searchTerm;
      }

      // Filtros
      if (filters.categoria) {
        params.categoria = filters.categoria;
      }

      const response = await glosarioAPI.getAll(params);
      const rawData = response.data;
      const data = Array.isArray(rawData) 
        ? rawData 
        : (rawData?.items || rawData?.data || []);

      // Filtrar por letra si está seleccionada
      let filteredData = data;
      if (filters.letra) {
        filteredData = data.filter((t) =>
          t.termino.toUpperCase().startsWith(filters.letra)
        );
      }

      setTerminos(filteredData);
      setTotalItems(filteredData.length);
      setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
    } catch (err) {
      setError(err.message || 'Error al cargar términos');
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    // Limpiar filtro de letra al buscar
    if (term) {
      setFilters({ ...filters, letra: '' });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
    setCurrentPage(1);
  };

  const handleLetraClick = (letra) => {
    setFilters({
      ...filters,
      letra: filters.letra === letra ? '' : letra,
    });
    setSearchTerm(''); // Limpiar búsqueda al filtrar por letra
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCreate = async (terminoData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await glosarioAPI.create(terminoData);

      setSuccess('Término creado exitosamente');
      setShowCreateModal(false);
      loadTerminos();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al crear término');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (terminoData) => {
    try {
      setFormLoading(true);
      setFormError(null);

      await glosarioAPI.update(selectedTermino.id_termino, terminoData);

      setSuccess('Término actualizado exitosamente');
      setShowEditModal(false);
      setSelectedTermino(null);
      loadTerminos();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al actualizar término');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setFormLoading(true);
      setFormError(null);

      await glosarioAPI.delete(selectedTermino.id_termino);

      setSuccess('Término eliminado exitosamente');
      setShowDeleteModal(false);
      setSelectedTermino(null);
      loadTerminos();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFormError(err.message || 'Error al eliminar término');
    } finally {
      setFormLoading(false);
    }
  };

  // Definir columnas de la tabla
  const columns = [
    {
      key: 'termino',
      label: 'Término',
      sortable: true,
      render: (value, termino) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center mr-3">
            <span className="text-sm font-bold text-white">
              {value?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {termino.definicion?.substring(0, 60)}...
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'categoria',
      label: 'Categoría',
      sortable: true,
      render: (value) => {
        const colors = {
          'Ética General': 'bg-blue-100 text-blue-800',
          'Ética Profesional': 'bg-purple-100 text-purple-800',
          'Bioética': 'bg-green-100 text-green-800',
          'Ética Digital': 'bg-indigo-100 text-indigo-800',
          'Derechos Humanos': 'bg-red-100 text-red-800',
          'Responsabilidad Social': 'bg-yellow-100 text-yellow-800',
          'Integridad': 'bg-pink-100 text-pink-800',
          'Transparencia': 'bg-cyan-100 text-cyan-800',
          'Compliance': 'bg-orange-100 text-orange-800',
          'Otros': 'bg-gray-100 text-gray-800',
        };
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              colors[value] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {value}
          </span>
        );
      },
    },
    {
      key: 'ejemplo',
      label: 'Tiene Ejemplo',
      render: (value) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {value ? 'Sí' : 'No'}
        </span>
      ),
    },
    {
      key: 'referencias',
      label: 'Tiene Referencias',
      render: (value) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {value ? 'Sí' : 'No'}
        </span>
      ),
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (_, termino) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedTermino(termino);
              setShowDetailModal(true);
            }}
            className="text-blue-600 hover:text-blue-900"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedTermino(termino);
              setShowEditModal(true);
            }}
            className="text-yellow-600 hover:text-yellow-900"
            title="Editar"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setSelectedTermino(termino);
              setShowDeleteModal(true);
            }}
            className="text-red-600 hover:text-red-900"
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Configuración de filtros
  const filterConfig = [
    {
      key: 'categoria',
      label: 'Categoría',
      type: 'select',
      value: filters.categoria,
      options: categorias.map((cat) => ({ value: cat, label: cat })),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Glosario de Términos Éticos</h1>
          <p className="mt-2 text-gray-600">
            Explora y gestiona el glosario de términos éticos
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nuevo Término
        </Button>
      </div>

      {/* Alertas */}
      {success && (
        <Alert type="success" message={success} onClose={() => setSuccess(null)} />
      )}
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      {/* Búsqueda y filtros */}
      <SearchBar
        placeholder="Buscar términos (búsqueda en término y definición)..."
        onSearch={handleSearch}
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Navegación alfabética */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Navegación Alfabética
        </h3>
        <div className="flex flex-wrap gap-2">
          {letras.map((letra) => (
            <button
              key={letra}
              onClick={() => handleLetraClick(letra)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                filters.letra === letra
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {letra}
            </button>
          ))}
          {filters.letra && (
            <button
              onClick={() => handleLetraClick('')}
              className="px-3 py-1 text-sm font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200"
            >
              Limpiar
            </button>
          )}
        </div>
      </Card>

      {/* Contador de resultados */}
      {searchTerm || filters.categoria || filters.letra ? (
        <div className="text-sm text-gray-600">
          {loading ? (
            'Buscando...'
          ) : (
            <>
              {totalItems} término{totalItems !== 1 ? 's' : ''} encontrado
              {totalItems !== 1 ? 's' : ''}
              {searchTerm && ` para "${searchTerm}"`}
              {filters.categoria && ` en categoría "${filters.categoria}"`}
              {filters.letra && ` que empiezan con "${filters.letra}"`}
            </>
          )}
        </div>
      ) : null}

      {/* Tabla */}
      <Table
        columns={columns}
        data={terminos}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        loading={loading}
        emptyMessage="No se encontraron términos"
      />

      {/* Paginación */}
      {!loading && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Modal Crear */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError(null);
        }}
        title="Crear Nuevo Término"
        size="lg"
      >
        <GlosarioForm
          onSubmit={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Editar */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTermino(null);
          setFormError(null);
        }}
        title="Editar Término"
        size="lg"
      >
        <GlosarioForm
          termino={selectedTermino}
          onSubmit={handleEdit}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedTermino(null);
            setFormError(null);
          }}
          loading={formLoading}
          error={formError}
        />
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedTermino(null);
        }}
        title="Detalle del Término"
        size="lg"
        footer={
          <Button
            onClick={() => {
              setShowDetailModal(false);
              setSelectedTermino(null);
            }}
          >
            Cerrar
          </Button>
        }
      >
        {selectedTermino && <TerminoDetail termino={selectedTermino} />}
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTermino(null);
          setFormError(null);
        }}
        title="Confirmar Eliminación"
        size="sm"
      >
        <div className="space-y-4">
          {formError && <Alert type="error" message={formError} />}

          <p className="text-gray-700">
            ¿Estás seguro de que deseas eliminar el término{' '}
            <strong>{selectedTermino?.termino}</strong>?
          </p>
          <p className="text-sm text-gray-600">
            Esta acción no se puede deshacer y el término será eliminado permanentemente.
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedTermino(null);
                setFormError(null);
              }}
              disabled={formLoading}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={formLoading}>
              Eliminar Término
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GlosarioPage;
